import { ModeEnum, NumericField, QuoteStatusEnum, toBig, Unit, type CustomerQuote } from '@talos/kyoko';
import type Big from 'big.js';
import type { CompositeBidAskFields, LegBidAskFields, PricingMethod } from './types';

/** Builds the initial BidAskFields constructs from a CustomerQuote */
export function customerQuoteToBidAskFields(cq: CustomerQuote): {
  compositeBidAsk: CompositeBidAskFields;
  legBidAsks: LegBidAskFields[];
  pricingMethod: PricingMethod;
} {
  const { disableBidFields, disableAskFields } = getFieldsToDisable(cq);
  const initialPricingMethod = getPricingMethodFromQuote(cq);

  // The backend sends Bid, Offer pxs as "0" despite us not having sent any price. "0" is still a valid price, so we don't want to show that unless that's actually what you've sent.
  const hasPopulatedPrices = cq.QuoteStatus !== QuoteStatusEnum.PendingNew;

  const legBidAsks: LegBidAskFields[] =
    cq.LegSummary?.map(leg => ({
      symbol: leg.Symbol,
      bid: getInitialPxField({
        px: hasPopulatedPrices ? leg.BidPx : undefined,
        unit: cq.unit,
        name: 'Customer Sell Price',
      })
        .setIsRequired(initialPricingMethod === 'per-leg' && !disableBidFields)
        .setDisabled(disableBidFields),
      ask: getInitialPxField({
        px: hasPopulatedPrices ? leg.OfferPx : undefined,
        unit: cq.unit,
        name: 'Customer Buy Price',
      })
        .setIsRequired(initialPricingMethod === 'per-leg' && !disableAskFields)
        .setDisabled(disableAskFields),
    })) ?? [];

  const sizeCounteringEnabled = cq.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const compositeBidAsk: CompositeBidAskFields = {
    symbol: cq.Symbol,
    bid: getInitialPxField({
      px: hasPopulatedPrices ? cq.BidPx : undefined,
      unit: cq.unit,
      name: 'Customer Sell Price',
    })
      .setIsRequired(initialPricingMethod === 'composite' && !disableBidFields)
      .setDisabled(disableBidFields),
    ask: getInitialPxField({
      px: hasPopulatedPrices ? cq.OfferPx : undefined,
      unit: cq.unit,
      name: 'Customer Buy Price',
    })
      .setIsRequired(initialPricingMethod === 'composite' && !disableAskFields)
      .setDisabled(disableAskFields),
    bidQty: getInitialQtyField({ qty: cq.bidQtyOrOrderQty, name: 'Customer Sell Qty' })
      .setMinimum('0')
      .setIsRequired(sizeCounteringEnabled && !disableBidFields)
      .setDisabled(disableBidFields),
    askQty: getInitialQtyField({ qty: cq.offerQtyOrOrderQty, name: 'Customer Buy Qty' })
      .setMinimum('0')
      .setIsRequired(sizeCounteringEnabled && !disableAskFields)
      .setDisabled(disableAskFields),
  };

  return {
    compositeBidAsk,
    legBidAsks,
    pricingMethod: initialPricingMethod,
  };
}

function getInitialPxField({ px, unit, name }: { px?: string; unit: Unit; name: string }): NumericField {
  return new NumericField({
    name,
    unit,
  })
    .updateValue(
      toBig(px)
        ?.times(unit === Unit.Bps ? 10_000 : 1)
        .toFixed() ?? ''
    )
    .setTouched(false);
}

function getInitialQtyField({ qty, name }: { qty: string; name: string }): NumericField {
  return new NumericField({
    name,
    unit: Unit.Decimal,
  })
    .updateValue(toBig(qty)?.toFixed())
    .setTouched(false);
}

export function getPricingMethodFromQuote(cq: CustomerQuote): PricingMethod {
  if (cq.QuoteStatus === QuoteStatusEnum.PendingNew) {
    return 'composite';
  }

  if (cq.BidPx == null && cq.OfferPx == null) {
    // Some other state, perhaps Rejected, where we haven't responded
    return 'composite';
  }

  if (cq.LegSummary == null || cq.LegSummary.length === 0) {
    // Spot / non-compound
    return 'composite';
  }

  // Else we are in a priced state, meaning that we have previously responded.
  // If we have responded once, we have either responded per-leg or composite. We can derive this by comparing the prices
  // When pricing top-down (composite), Talos will put the composite Bid onto every Leg, so we can reverse engineer that by doing the below (for now)
  const firstLegBidPx = cq.LegSummary.at(0)?.BidPx;
  const firstLegOfferPx = cq.LegSummary.at(0)?.OfferPx;
  const allBidsEqual = cq.LegSummary?.every(leg => leg.BidPx === firstLegBidPx);
  const allOffersEqual = cq.LegSummary?.every(leg => leg.OfferPx === firstLegOfferPx);
  const isCompositePricing = allBidsEqual && allOffersEqual;
  return isCompositePricing ? 'composite' : 'per-leg';
}

/** Given a customer quote, will check the status and side of the quote, and return which fields should be disabled */
export function getFieldsToDisable(cq: CustomerQuote) {
  const disableAll = !cq.isOpen;
  const disableBidFields = disableAll || cq.Side === 'Buy';
  const disableAskFields = disableAll || cq.Side === 'Sell';

  return { disableBidFields, disableAskFields, disableOtherFields: disableAll };
}

/** Returns whether or not the bid is greater than the ask. If either bid or ask are nullish, returns true (there is no spread, so it is valid). */
export function isSpreadValid({ bid, ask }: { bid: Big | undefined; ask: Big | undefined }): boolean {
  if (!bid || !ask) {
    return true;
  }

  return bid.lte(ask);
}
